import ExcelJS from 'exceljs';
import {
  COMMON_SHEET_MAP,
  CLF_BASIC_SHEET_MAP,
  CLF_COMMUTATION_SHEET_MAP,
  commonFields,
  clfBasicFields,
  clfCommutationFields,
  PARSED_CONFIG_TEMPLATE,
  COMMON_WORKSHEET_COLUMNS,
  CLF_BASIC_WORKSHEET_COLUMNS,
  CLF_COMMUTATION_WORKSHEET_COLUMNS,
  WORKSHEET_PROTECTION_OPTIONS,
  INFO_ROW_TEXT,
  EMPTY_ROWS_COUNT,
} from './constants';
import { cleanSheet, validateSheetHeaders } from '../../../../../externalUtils';
import { createWorkbook, downloadExcel } from '../../../../../utils';

const parseSheetRows = ({ validRows, fields, sheetMap, sheetName, target }, errors, rowValid) => {
  for (const [index, row] of validRows.entries()) {
    if (index === 0) {
      for (const field of fields) {
        const value = row[sheetMap[field.key]];
        if (field.required && !value) {
          rowValid = false;
          errors.push(`Missing "${field.key}" value in the first row of the ${sheetName} sheet`);
        }
      }
    }
    if (rowValid) {
      for (const field of fields) {
        const value = row[sheetMap[field.key]];
        if (value) {
          if (field.isNumber) {
            if (typeof value === 'number') {
              target[field.configKey] = Number(value);
            } else {
              errors.push(`"${field.key}" value in the ${sheetName} sheet is not a number`);
              rowValid = false;
            }
          } else if (field.isArray) {
            target[field.configKey].push(String(value));
          } else {
            target[field.configKey] = String(value);
          }
        }
      }
    }
  }
  return rowValid;
};

const stripEmptyArrays = (object) => {
  const strippedObject = structuredClone(object);
  for (const key in strippedObject) {
    if (Array.isArray(strippedObject[key]) && strippedObject[key].length === 0) {
      delete strippedObject[key];
    }
    if (typeof strippedObject[key] === 'object' && strippedObject[key] !== null) {
      strippedObject[key] = stripEmptyArrays(strippedObject[key]);
    }
  }
  return strippedObject;
};

export const parseTFRClassificationConfigTemplate = async (signalConfig) => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(signalConfig);

  const commonSheet = workbook.worksheets.find((s) => s.name === 'common');
  const clf_basicSheet = workbook.worksheets.find((s) => s.name === 'clf_basic');
  const clf_commutationSheet = workbook.worksheets.find((s) => s.name === 'clf_commutation');

  const errors = [];

  if (!commonSheet) {
    errors.push('No common sheet found');
    return { errors };
  }

  const [commonSheetHeaders, ...commonValidRows] = cleanSheet(commonSheet, true);

  if (!commonValidRows || commonValidRows.length === 0) {
    errors.push('common sheet was empty');
    return { errors };
  }

  validateSheetHeaders(commonSheetHeaders, commonSheet, COMMON_SHEET_MAP, errors);

  let clf_basicValidRows = [];
  if (clf_basicSheet) {
    const [clf_basicSheetHeaders, ...rows] = cleanSheet(clf_basicSheet, true);
    clf_basicValidRows = rows;
    if (rows.length > 0) {
      validateSheetHeaders(clf_basicSheetHeaders, clf_basicSheet, CLF_BASIC_SHEET_MAP, errors);
    }
  }

  let clf_commutationValidRows = [];
  if (clf_commutationSheet) {
    const [clf_commutationSheetHeaders, ...rows] = cleanSheet(clf_commutationSheet, true);
    clf_commutationValidRows = rows;
    if (rows.length > 0) {
      validateSheetHeaders(
        clf_commutationSheetHeaders,
        clf_commutationSheet,
        CLF_COMMUTATION_SHEET_MAP,
        errors
      );
    }
  }

  if (errors.length > 0) {
    return { errors };
  }

  const parsedConfig = structuredClone(PARSED_CONFIG_TEMPLATE);

  let rowValid = true;

  const sheetConfigs = [
    {
      sheetName: 'common',
      validRows: commonValidRows,
      fields: commonFields,
      sheetMap: COMMON_SHEET_MAP,
      target: parsedConfig.common,
    },
  ];

  if (clf_basicValidRows.length > 0) {
    sheetConfigs.push({
      sheetName: 'clf_basic',
      validRows: clf_basicValidRows,
      fields: clfBasicFields,
      sheetMap: CLF_BASIC_SHEET_MAP,
      target: parsedConfig.clf_basic,
    });
  }

  if (clf_commutationValidRows.length > 0) {
    sheetConfigs.push({
      sheetName: 'clf_commutation',
      validRows: clf_commutationValidRows,
      fields: clfCommutationFields,
      sheetMap: CLF_COMMUTATION_SHEET_MAP,
      target: parsedConfig.clf_commutation,
    });
  }

  for (const config of sheetConfigs) {
    rowValid = parseSheetRows(config, errors, rowValid);
  }

  if (clf_basicValidRows.length === 0) {
    delete parsedConfig.clf_basic;
  }
  if (clf_commutationValidRows.length === 0) {
    delete parsedConfig.clf_commutation;
  }

  return rowValid
    ? { fileName: signalConfig.name, parsedConfig: stripEmptyArrays(parsedConfig), errors }
    : { errors };
};

const styleHeaderRow = (worksheet) => {
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { horizontal: 'center' };

  headerRow.eachCell({ includeEmpty: true }, (cell) => {
    const updatedCell = cell;
    updatedCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'D9D9D9' },
    };
    updatedCell.protection = { locked: true };
  });
};

const addInfoRow = (worksheet, columnCount) => {
  const infoRow = worksheet.addRow([INFO_ROW_TEXT]);
  infoRow.font = { italic: true, color: { argb: 'FF0000FF' }, border: '1px solid red' };
  infoRow.height = 55;
  infoRow.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
  infoRow.eachCell({ includeEmpty: true }, (cell) => {
    const updatedCell = cell;
    updatedCell.protection = { locked: true };
  });

  // Merge cells based on the number of columns in the worksheet
  const lastColumnLetter = String.fromCodePoint(64 + columnCount);
  worksheet.mergeCells(`A${infoRow.number}:${lastColumnLetter}${infoRow.number}`);
};

const addEmptyRows = (worksheet, columnCount) => {
  // Add empty rows for data entry
  const emptyRow = Array.from({ length: columnCount }).fill('');
  for (let index = 0; index < EMPTY_ROWS_COUNT; index += 1) {
    const dataRow = worksheet.addRow(emptyRow);
    dataRow.eachCell({ includeEmpty: true }, (cell) => {
      const updatedCell = cell;
      updatedCell.protection = { locked: false };
    });
  }
};

export const generateTFRClassificationConfigTemplate = () => {
  const workbook = createWorkbook();
  const commonWorksheet = workbook.addWorksheet('common');
  const clfBasicWorksheet = workbook.addWorksheet('clf_basic');
  const clfCommutationWorksheet = workbook.addWorksheet('clf_commutation');

  commonWorksheet.columns = COMMON_WORKSHEET_COLUMNS;
  clfBasicWorksheet.columns = CLF_BASIC_WORKSHEET_COLUMNS;
  clfCommutationWorksheet.columns = CLF_COMMUTATION_WORKSHEET_COLUMNS;

  [commonWorksheet, clfBasicWorksheet, clfCommutationWorksheet].forEach((worksheet) => {
    const columnCount = worksheet.columns.length;

    styleHeaderRow(worksheet);
    addInfoRow(worksheet, columnCount);
    addEmptyRows(worksheet, columnCount);

    worksheet.protect('', WORKSHEET_PROTECTION_OPTIONS);
  });

  downloadExcel('xlsx', workbook, 'SignalConfigTemplate.xlsx');
};
